import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Target, CheckSquare, BarChart3, LogOut, Upload } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';

const Sidebar = () => {
  const location = useLocation();
  const { user, logout } = useAuth();
  
  const isManager = user?.role === 'manager';

  // Role-based navigation items
  const navItems = isManager
    ? [
        { path: '/dashboard', icon: BarChart3, label: 'Dashboard' },
        { path: '/training', icon: Target, label: 'Training' },
        { path: '/qa', icon: CheckSquare, label: 'QA Scorer' },
        { path: '/import', icon: Upload, label: 'Import Hub' },
      ]
    : [
        { path: '/dashboard', icon: BarChart3, label: 'Dashboard' },
        { path: '/training', icon: Target, label: 'Training' },
      ];

  const isActive = (path) => location.pathname === path;

  const getInitials = (name) => {
    if (!name) return '??';
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="fixed top-0 left-0 bottom-0 w-56 bg-[#0d0f12] border-r border-[#2a3040] flex flex-col z-50">
      {/* Logo */}
      <Link to="/dashboard" className="flex items-center gap-3 px-5 py-5 border-b border-[#2a3040] group">
        <img 
          src="/agentiq-logo-full.webp" 
          alt="AgentIQ Logo" 
          className="h-9 w-auto object-contain group-hover:scale-105 transition-transform"
        />
      </Link>

      {/* Navigation */}
      <nav className="flex-1 px-2.5 py-3 overflow-y-auto">
        <div className="text-[10px] font-mono tracking-wider text-[#555f72] uppercase px-3 py-2 mb-1">
          Menu
        </div>
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg mb-1 text-sm font-medium transition-all ${
                active
                  ? 'bg-[#1e2229] text-[#4f8ef7]'
                  : 'text-[#8b95a8] hover:bg-[#161920] hover:text-white'
              }`}
            >
              <Icon className="w-5 h-5" />
              <span>{item.label}</span>
            </Link>
          );
        })}
      </nav>

      {/* User Profile */}
      <div className="px-2.5 py-3 border-t border-[#2a3040]">
        <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#4f8ef7] to-[#9b6dff] flex items-center justify-center text-xs font-semibold text-white">
            {getInitials(user?.name)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-medium text-white truncate">
              {user?.name || 'User'}
            </div>
            <div className="flex items-center gap-1.5 mt-0.5">
              <div className={`text-[10px] px-1.5 py-0.5 rounded ${
                isManager 
                  ? 'bg-[#4f8ef7]/10 text-[#4f8ef7] border border-[#4f8ef7]/30' 
                  : 'bg-[#34c78a]/10 text-[#34c78a] border border-[#34c78a]/30'
              }`}>
                {isManager ? 'Manager' : 'Agent'}
              </div>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={logout}
            className="w-7 h-7 p-0 text-[#555f72] hover:text-[#f05252] hover:bg-[#f05252]/10 hover:border-[#f05252]"
            title="Logout"
          >
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
